import { Panel } from './Panel';
import { formatNumber } from '@/lib/utils';
import type { Character } from '@/types/database';

interface SkillsPanelProps {
  character: Character;
}

function xpToNextLevel(level: number): number {
  return 100 * level;
}

interface SkillRowProps {
  label: string;
  level: number;
  xp: number;
}

function SkillRow({ label, level, xp }: SkillRowProps): JSX.Element {
  const nextLevelAt = xpToNextLevel(level);
  const progress = Math.max(0, Math.min(xp, nextLevelAt));
  const percent = (progress / Math.max(1, nextLevelAt)) * 100;

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-sm text-ink">{label}</span>
        <span className="text-xs text-ink-light">
          Lv <span className="font-display text-base text-wood-dark font-semibold">{level}</span>
          <span className="mx-1 text-wood-mid/50">&middot;</span>
          <span className="font-mono text-ink-mid">
            {formatNumber(progress)}/{formatNumber(nextLevelAt)}
          </span>
        </span>
      </div>
      <div
        className="h-2.5 w-full overflow-hidden rounded-full border border-wood-mid/40 bg-parchment-dark/40 shadow-inner"
        role="progressbar"
        aria-label={`${label} XP to next level`}
        aria-valuenow={progress}
        aria-valuemin={0}
        aria-valuemax={nextLevelAt}
      >
        <div
          className="h-full rounded-full bg-candle-dark transition-[width] duration-700 ease-out"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

export function SkillsPanel({ character }: SkillsPanelProps): JSX.Element {
  return (
    <Panel title="Skills">
      <div className="space-y-4">
        <SkillRow
          label="Crime Skill"
          level={character.crime_skill_level}
          xp={character.crime_skill_xp}
        />
        <SkillRow
          label="Sea Crime Skill"
          level={character.sea_crime_skill_level}
          xp={character.sea_crime_skill_xp}
        />
        <SkillRow
          label="Seamanship"
          level={character.seamanship_level}
          xp={character.seamanship_xp}
        />
      </div>
    </Panel>
  );
}
